import React, { useEffect, useId, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { registerModal, lockBodyScroll } from '../utils/modalStack';

/**
 * Reusable modal component with backdrop, header, content, and optional footer.
 *
 * @param {boolean}    isOpen       - Whether modal is visible
 * @param {Function}   onClose      - Close handler
 * @param {string}     title        - Modal title
 * @param {ReactNode}  [titleIcon]  - Optional icon next to title
 * @param {string}     [subtitle]   - Optional subtitle below title
 * @param {ReactNode}  children     - Modal body content
 * @param {ReactNode}  [footer]     - Optional footer (action buttons)
 * @param {string}     [maxWidth]   - Tailwind max-w class (default: 'max-w-lg')
 * @param {boolean}    [closeOnBackdrop] - Close when clicking backdrop (default: true)
 * @param {boolean}    [closeOnEscape]   - Close on Escape (default: true). Only the TOPMOST open
 *                                         overlay (Modal or ConfirmModal) answers Escape, see
 *                                         utils/modalStack.js. `false` still shields the overlays
 *                                         beneath: Escape does nothing rather than closing them.
 * @param {string}     [className]  - Additional classes for the modal container
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Attribute-based (jsdom has no layout): skips hidden / inert / aria-hidden subtrees, which the
// selector alone cannot express.
const focusables = root =>
  Array.from(root.querySelectorAll(FOCUSABLE)).filter(el => !el.closest('[hidden], [inert], [aria-hidden="true"]'));

const Modal = ({
  isOpen,
  onClose,
  title,
  titleIcon,
  subtitle,
  children,
  footer,
  maxWidth = 'max-w-lg',
  closeOnBackdrop = true,
  closeOnEscape = true,
  className = '',
}) => {
  // The stack entry outlives renders, so it reads the latest props through a ref
  // instead of re-registering (which would move this overlay to the top) whenever
  // a caller passes a fresh inline onClose.
  const escapeRef = useRef(null);
  escapeRef.current = () => {
    if (closeOnEscape) onClose();
  };

  const titleId = useId();
  const dialogRef = useRef(null);
  const entryRef = useRef(null);
  // The element focused BEFORE the dialog opened. Captured during render, on the closed -> open
  // transition: a child's autoFocus runs in the commit phase, before any passive effect, so reading
  // document.activeElement in an effect would record the autoFocus input instead of the opener.
  const openerRef = useRef(null);
  if (!isOpen) openerRef.current = null;
  else if (!openerRef.current && typeof document !== 'undefined') openerRef.current = document.activeElement;

  useEffect(() => {
    if (!isOpen) return undefined;
    const entry = registerModal(() => escapeRef.current());
    entryRef.current = entry;
    return () => {
      entryRef.current = null;
      entry.unregister();
    };
  }, [isOpen]);

  // Focus: move into the dialog on open (unless a child already took focus, e.g. autoFocus),
  // give it back to the opener on close/unmount.
  useEffect(() => {
    if (!isOpen) return undefined;
    const opener = openerRef.current;
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) {
      const first = focusables(dialog)[0];
      (first || dialog).focus();
    }
    return () => {
      // Known limit: if the opener left the DOM while the dialog was open there is nothing to
      // return to, so focus falls to <body>. The guard only keeps that from throwing.
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    };
  }, [isOpen]);

  // Tab trap. Only the topmost overlay answers; a lower Modal leaves Tab alone.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = e => {
      if (e.key !== 'Tab' || e.defaultPrevented) return;
      const entry = entryRef.current;
      const dialog = dialogRef.current;
      if (!entry || !entry.isTop() || !dialog) return;
      const items = focusables(dialog);
      if (items.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === dialog || !dialog.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !dialog.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  useEffect(() => {
    // Ref-counted (modalStack.js): closing the top of two stacked Modals keeps the
    // page locked for the one still open.
    if (!isOpen) return undefined;
    return lockBodyScroll();
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50" aria-hidden="true" onClick={closeOnBackdrop ? onClose : undefined} />

        {/* Modal container */}
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          className={`relative bg-white dark:bg-gray-800 rounded-xl shadow-xl ${maxWidth} w-full z-10 max-h-[90vh] flex flex-col focus:outline-none ${className}`}
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
            <div>
              <h2
                id={titleId}
                className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2"
              >
                {titleIcon}
                {title}
              </h2>
              {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>}
            </div>
            <button
              // Without this the button defaults to type="submit". A Modal is rendered
              // inline (there is no portal), so any Modal opened from inside a <form>
              // — e.g. the marketing picker in EmailTemplateEditor's block editor —
              // would SUBMIT that form when the user clicked the close X.
              type="button"
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 flex-shrink-0"
              aria-label="Close"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto flex-1">{children}</div>

          {/* Footer */}
          {footer && (
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 flex-shrink-0 bg-gray-50 dark:bg-gray-900 rounded-b-xl">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Standard cancel button for modal footers.
 */
export const ModalCancelButton = ({ onClick, disabled, children = 'Cancel' }) => (
  <button
    // Same reason as the close X above: no portal, so this frequently renders
    // inside a <form>, where an untyped button submits. Every caller passes
    // onClick, so submitting is never the intent.
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
  >
    {children}
  </button>
);

/**
 * Standard primary action button for modal footers.
 */
export const ModalActionButton = ({
  onClick,
  disabled,
  loading,
  loadingText,
  color = 'sage',
  icon,
  children,
  // Anything else (`ref`, `title`, `aria-keyshortcuts`, ...) goes to the button itself.
  ...buttonProps
}) => {
  const colorClasses = {
    sage: 'bg-sage-600 hover:bg-sage-700',
    emerald: 'bg-emerald-600 hover:bg-emerald-700',
    red: 'bg-red-600 hover:bg-red-700',
    blue: 'bg-blue-600 hover:bg-blue-700',
    indigo: 'bg-indigo-600 hover:bg-indigo-700',
    purple: 'bg-purple-600 hover:bg-purple-700',
  };

  return (
    <button
      {...buttonProps}
      onClick={onClick}
      disabled={disabled || loading}
      className={`px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${colorClasses[color] || colorClasses.sage}`}
    >
      {loading ? (
        <>
          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          {loadingText || 'Saving...'}
        </>
      ) : (
        <>
          {icon}
          {children}
        </>
      )}
    </button>
  );
};

export default Modal;
